import { useState, useEffect, useCallback, Suspense } from "react";
import DashboardLayout from "../../components/DashboardLayout";
import {
  EventHeader,
  EventDetails,
  ClientInfo,
  UnifiedPaymentSection,
  VendorPaymentsSection,
  DetailedItemAllocations,
  ClientPaymentModal,
  AddServiceModal,
  AddVendorModal,
  EventActivityTimeline,
  GiveVendorPaymentModal,
  RoomBookingsSection,
  AddRoomBookingModal,
  EditPaymentModal,
} from "../components";
import { EventDetail, Task, ClientPayment } from "../components/types";
import { eventsApi, clientPaymentsApi } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/hooks/useAuth";
import { Loader } from "lucide-react";

function EventDetailPageContent() {
  const { showToast } = useToast();
  const { user } = useAuth();
  const isStaff = user?.role === "staff";
  const isAdmin = ["super_admin", "admin", "master"].includes(user?.role ?? "");

  const eventId = typeof window !== "undefined"
    ? new URLSearchParams(window.location.search).get("id")
    : null;

  const [event, setEvent] = useState<EventDetail | null>(null);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [logs, setLogs] = useState<any[]>([]);
  const [customerPayments, setCustomerPayments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modal state
  const [isClientPaymentModalOpen, setIsClientPaymentModalOpen] = useState(false);
  const [isAddServiceModalOpen, setIsAddServiceModalOpen] = useState(false);
  const [isAddVendorModalOpen, setIsAddVendorModalOpen] = useState(false);
  const [isVendorPaymentModalOpen, setIsVendorPaymentModalOpen] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState<any>(null);
  const [isAddRoomModalOpen, setIsAddRoomModalOpen] = useState(false);
  const [editingPayment, setEditingPayment] = useState<ClientPayment | null>(null);

  // Key that VendorPaymentsSection watches to trigger its own re-fetch
  const [vendorPaymentsRefreshKey, setVendorPaymentsRefreshKey] = useState(0);

  // ── Data loading ────────────────────────────────────────────────────────────

  const loadPayments = useCallback(async (evId: number) => {
    try {
      const res = await clientPaymentsApi.list({ event_id: evId });
      setCustomerPayments(res.data?.data || res.data || []);
    } catch {
      // keep existing state
    }
  }, []);

  const loadEventData = useCallback(async () => {
    if (!eventId) {
      setError("No event ID provided");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await eventsApi.get(parseInt(eventId));
      const eventData: EventDetail = response.data?.data || response.data;

      if (!eventData?.id) {
        setError("Event not found");
        setEvent(null);
        return;
      }
      if (eventData.id !== parseInt(eventId)) {
        setError("Event ID mismatch");
        setEvent(null);
        return;
      }

      setEvent(eventData);

      // Load tasks, logs, payments in parallel
      const [tasksResult, logsResult] = await Promise.allSettled([
        eventsApi.getTasks(eventData.id),
        eventsApi.getLogs?.(eventData.id),
      ]);

      if (tasksResult.status === "fulfilled" && tasksResult.value) {
        setTasks(tasksResult.value.data?.data || tasksResult.value.data || []);
      }
      if (logsResult.status === "fulfilled" && logsResult.value) {
        setLogs(logsResult.value.data?.data || logsResult.value.data || []);
      }

      await loadPayments(eventData.id);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to load event details";
      setError(message);
      showToast(message, "error");
      setEvent(null);
    } finally {
      setLoading(false);
    }
  }, [eventId, showToast, loadPayments]);

  useEffect(() => {
    loadEventData();
  }, [loadEventData]);

  // ── Targeted refresh helpers ─────────────────────────────────────────────────

  /** Re-fetch just the event financials/core data without flickering the page. */
  const refreshEvent = useCallback(async () => {
    if (!eventId) return;
    try {
      const response = await eventsApi.get(parseInt(eventId));
      const eventData: EventDetail = response.data?.data || response.data;
      if (eventData?.id) setEvent(eventData);
    } catch {
      // non-fatal — page still has previous state
    }
  }, [eventId]);

  /** Re-fetch client payments only. */
  const refreshPayments = useCallback(async () => {
    if (!event?.id) return;
    await loadPayments(event.id);
  }, [event?.id, loadPayments]);

  /** Trigger VendorPaymentsSection to re-fetch its own data. */
  const refreshVendorPayments = () => setVendorPaymentsRefreshKey((k) => k + 1);

  // ── Derived financial summary for EventHeader ────────────────────────────────

  const financialSummary = event?.financials
    ? (() => {
        const { services = [], vendors = [], rooms = [], customer } = event.financials;
        const billed =
          services.reduce((s: number, x: any) => s + (x.billed || 0), 0) +
          vendors.reduce((s: number, x: any) => s + (x.billed || 0), 0) +
          rooms.reduce((s: number, x: any) => s + (x.amount || 0), 0);
        const cost =
          services.reduce((s: number, x: any) => s + (x.cost ?? 0), 0) +
          vendors.reduce((s: number, x: any) => s + (x.cost ?? 0), 0);
        const paid = (customerPayments || []).reduce(
          (s: number, p: any) => s + (Number(p.amount) || 0),
          0
        );
        const total = customer?.total || billed;
        return {
          total,
          paid,
          balance: total - paid,
          profit: billed - cost,
        };
      })()
    : undefined;

  // ── Event handlers ───────────────────────────────────────────────────────────

  const handleVendorPaymentFromSection = (vendor: any) => {
    setSelectedVendor({
      vendor_id: vendor.vendor_id,
      vendor_name: vendor.vendor_name,
      total_amount: vendor.total_amount,
      paid_amount: vendor.paid_amount,
      balance: vendor.balance,
      items: vendor.items,
      payments: vendor.payments,
    });
    setIsVendorPaymentModalOpen(true);
  };

  // ── Loading / error states ───────────────────────────────────────────────────

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-screen">
          <Loader className="w-8 h-8 animate-spin text-stone-400" />
        </div>
      </DashboardLayout>
    );
  }

  if (error || !event) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <p className="text-red-600 font-medium mb-4">{error || "Event not found"}</p>
            <button
              onClick={() => window.history.back()}
              className="px-4 py-2 bg-stone-200 text-stone-700 rounded-lg hover:bg-stone-300 transition-colors"
            >
              Go Back
            </button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        <EventHeader
          event={event}
          financials={financialSummary}
          onAddService={() => setIsAddServiceModalOpen(true)}
          onAddVendor={() => setIsAddVendorModalOpen(true)}
        />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* ── Main column ── */}
          <div className="lg:col-span-2 space-y-6">
            {event.financials && (
              <DetailedItemAllocations
                services={event.financials.services || []}
                vendors={event.financials.vendors || []}
                rooms={event.financials.rooms || []}
                onAddService={() => setIsAddServiceModalOpen(true)}
                onAddVendor={() => setIsAddVendorModalOpen(true)}
                onAddRoom={() => setIsAddRoomModalOpen(true)}
                onSwitchVendor={(item, type) => {
                  showToast(`Switch vendor for ${type}: ${item.name}`, "info");
                }}
                onDeleteItem={(item, type) => {
                  showToast(`Delete ${type}: ${item.name}`, "info");
                }}
              />
            )}

            <RoomBookingsSection
              eventId={event.id}
              rooms={event.financials?.rooms || []}
              onAdd={() => setIsAddRoomModalOpen(true)}
              onDelete={async (id) => {
                if (!window.confirm("Remove this room booking? This cannot be undone.")) return;
                try {
                  await eventsApi.removeRoomBooking(id);
                  showToast("Room booking removed", "success");
                  await refreshEvent();
                } catch {
                  showToast("Failed to remove room booking", "error");
                }
              }}
            />

            <div className="border-t border-stone-200 pt-6">
              <VendorPaymentsSection
                eventId={event.id}
                vendors={event.financials?.vendors || []}
                onPayClick={() => showToast("Select a vendor to make a payment", "info")}
                onPayVendor={handleVendorPaymentFromSection}
                refreshKey={vendorPaymentsRefreshKey}
              />
            </div>

            {event.financials && (
              <div className="border-t border-stone-200 pt-6">
                <UnifiedPaymentSection
                  customer={event.financials.customer}
                  payments={customerPayments.length > 0 ? customerPayments : event.clientPayments}
                  onAddPayment={() => setIsClientPaymentModalOpen(true)}
                  canEditPayments={isAdmin}
                  onEditPayment={(payment) => setEditingPayment(payment)}
                  event={{
                    id: event.id,
                    name: event.name,
                    start_date: event.start_date,
                    venue: event.venue,
                    client: event.client,
                  }}
                />
              </div>
            )}
          </div>

          {/* ── Sidebar ── */}
          <div className="space-y-6">
            <EventDetails
              event={event}
              tasksCount={tasks.length}
              invoicesCount={event.invoices?.length || 0}
            />

            {event.client && <ClientInfo client={event.client} />}

            <EventActivityTimeline logs={logs} />
          </div>
        </div>
      </div>

      {/* ── Modals ── */}
      {editingPayment && (
        <EditPaymentModal
          isOpen={!!editingPayment}
          onClose={() => setEditingPayment(null)}
          payment={editingPayment}
          onUpdated={async () => {
            setEditingPayment(null);
            await refreshPayments();
          }}
        />
      )}

      {isClientPaymentModalOpen && (
        <ClientPaymentModal
          isOpen={isClientPaymentModalOpen}
          onClose={() => setIsClientPaymentModalOpen(false)}
          eventId={event.id}
          customer={event.financials?.customer}
          payments={event.clientPayments || []}
          onPaymentAdded={async () => {
            setIsClientPaymentModalOpen(false);
            await refreshPayments();
          }}
          event={{
            id: event.id,
            name: event.name,
            start_date: event.start_date,
            venue: event.venue,
            client: event.client,
          }}
        />
      )}

      {isAddServiceModalOpen && (
        <AddServiceModal
          isOpen={isAddServiceModalOpen}
          onClose={() => setIsAddServiceModalOpen(false)}
          eventId={event.id}
          onServiceAdded={async () => {
            setIsAddServiceModalOpen(false);
            await refreshEvent();
          }}
        />
      )}

      {isAddVendorModalOpen && (
        <AddVendorModal
          isOpen={isAddVendorModalOpen}
          onClose={() => setIsAddVendorModalOpen(false)}
          eventId={event.id}
          onVendorAdded={async () => {
            setIsAddVendorModalOpen(false);
            await refreshEvent();
          }}
        />
      )}

      <AddRoomBookingModal
        isOpen={isAddRoomModalOpen}
        onClose={() => setIsAddRoomModalOpen(false)}
        eventId={event.id}
        onBookingAdded={async () => {
          setIsAddRoomModalOpen(false);
          await refreshEvent();
        }}
      />

      {isVendorPaymentModalOpen && selectedVendor && (
        <GiveVendorPaymentModal
          isOpen={isVendorPaymentModalOpen}
          onClose={() => {
            setIsVendorPaymentModalOpen(false);
            setSelectedVendor(null);
          }}
          eventId={event.id}
          vendor={selectedVendor}
          onPaymentAdded={async () => {
            setIsVendorPaymentModalOpen(false);
            setSelectedVendor(null);
            refreshVendorPayments();
          }}
        />
      )}
    </DashboardLayout>
  );
}

function LoadingFallback() {
  return (
    <DashboardLayout>
      <div className="flex items-center justify-center min-h-screen">
        <Loader className="w-8 h-8 animate-spin text-stone-400" />
      </div>
    </DashboardLayout>
  );
}

export default function EventDetailPage() {
  return (
    <Suspense fallback={<LoadingFallback />}>
      <EventDetailPageContent />
    </Suspense>
  );
}
